"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const faqs = [{
  question: "Do you come to my *home* or office?",
  answer: "Yes, we are a fully mobile detailing service. We bring our own water and power supply to your location, whether it is your home driveway or your office parking garage."
}, {
  question: "How long does a *service* take?",
  answer: "Depending on the package and vehicle condition, services typically range from 2 to 6 hours. We provide an estimated time frame upon arrival for your specific vehicle."
}, {
  question: "What is your *cancellation* policy?",
  answer: "We require 24 hours notice for cancellations. Appointments canceled within 24 hours may be subject to a service fee to cover reserved technician time and travel logistics."
}, {
  question: "Are *children* or pets safe?",
  answer: "We use premium, eco-friendly, and non-toxic products that are safe for your family and pets. We recommend keeping them away from the vehicle while we are working."
}, {
  question: "Is *payment* due upfront?",
  answer: "Payment is processed securely upon completion of the service. We accept all major credit cards, digital wallets, and Zelle for your convenience at the time of service."
}, {
  question: "Do you offer *monthly* plans?",
  answer: "Yes, we offer recurring maintenance plans to keep your vehicle in showroom condition year-round. Contact us to discuss a custom schedule that fits your driving habits."
}];
function renderQuestion(text: string) {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i} className="font-serif italic" style={{
        color: "var(--primary)"
      }}>
          {part.slice(1, -1)}
        </em>;
    }
    return <span key={i}>{part}</span>;
  });
}
export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const prefersReducedMotion = useReducedMotion();
  const toggle = (i: number) => {
    setOpenIndex(prev => prev === i ? null : i);
  };
  return <section data-section-id="28744" id="luxuryaccordionfaq" className="bg-background text-foreground py-32 md:py-44 px-6 md:px-12">
      <div className="mx-auto max-w-[820px]">
        {}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="mb-20 text-center mx-auto max-w-[720px]">
          <p className="font-sans uppercase mb-8" style={{
          color: "var(--muted-foreground)",
          fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)",
          letterSpacing: "0.22em",
          fontWeight: 500
        }}>
            Common Questions
          </p>
          <h2 className="font-serif mb-8" style={{
          fontSize: "clamp(2.5rem, 5vw, 4.75rem)",
          fontWeight: 400,
          letterSpacing: "-0.018em",
          lineHeight: 1.02,
          color: "var(--foreground)"
        }}>
            Expert{" "}
            <em className="font-serif italic" style={{
            color: "var(--primary)"
          }}>
              Guidance
            </em>
          </h2>
          <p className="font-sans mx-auto" style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
          lineHeight: 1.6,
          color: "var(--muted-foreground)",
          maxWidth: "560px"
        }}>
            {"For anything not answered here, our team is glad to help — write to us at "}
            <Link to="/ContactPage" className="underline underline-offset-4 transition-colors duration-300 hover:text-primary" style={{
            textDecorationColor: "var(--primary)",
            color: "var(--foreground)"
          }}>
              our contact page
            </Link>
            {"."}
          </p>
        </motion.div>

        {}
        <motion.ul initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: prefersReducedMotion ? 0 : 0.08,
            delayChildren: 0.1
          }
        }
      }} className="list-none p-0 m-0">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          const panelId = `faq-panel-${i}`;
          const buttonId = `faq-button-${i}`;
          return <motion.li data-index={i} key={i} variants={{
            hidden: prefersReducedMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 16
            },
            show: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1]
              }
            }
          }} className="border-b" style={{
            borderColor: "var(--border)"
          }}>
                <h3 className="m-0">
                  <Button id={buttonId} variant="ghost" onClick={() => toggle(i)} aria-expanded={isOpen} aria-controls={panelId} className="w-full h-auto flex items-start justify-between gap-6 py-8 px-0 bg-transparent hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-4 text-left" style={{
                outlineColor: "var(--primary)"
              }}>
                    <span className="font-serif flex-1 text-left" style={{
                  fontSize: "clamp(1.25rem, 2.2vw, 2rem)",
                  fontWeight: 400,
                  letterSpacing: "-0.012em",
                  lineHeight: 1.2,
                  color: "var(--foreground)",
                  whiteSpace: "normal"
                }}>
                      {renderQuestion(faq.question)}
                    </span>
                    <motion.span aria-hidden="true" animate={{
                  rotate: isOpen ? 45 : 0
                }} transition={{
                  duration: prefersReducedMotion ? 0 : 0.32,
                  ease: [0.4, 0, 0.2, 1]
                }} className="inline-flex items-center justify-center shrink-0 mt-1 rounded-lg border transition-colors duration-300" style={{
                  width: "clamp(32px, 4vw, 36px)",
                  height: "clamp(32px, 4vw, 36px)",
                  borderColor: isOpen ? "var(--primary)" : "var(--border)",
                  backgroundColor: isOpen ? "var(--accent)" : "transparent",
                  color: "var(--primary)"
                }}>
                      <Plus className="size-4" strokeWidth={1.25} />
                    </motion.span>
                  </Button>
                </h3>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div id={panelId} role="region" aria-labelledby={buttonId} key="content" initial={prefersReducedMotion ? {
                opacity: 0,
                height: 0
              } : {
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto",
                transition: {
                  height: {
                    duration: prefersReducedMotion ? 0 : 0.32,
                    ease: [0.4, 0, 0.2, 1]
                  },
                  opacity: {
                    duration: prefersReducedMotion ? 0 : 0.24,
                    delay: prefersReducedMotion ? 0 : 0.08
                  }
                }
              }} exit={{
                opacity: 0,
                height: 0,
                transition: {
                  height: {
                    duration: prefersReducedMotion ? 0 : 0.32,
                    ease: [0.4, 0, 0.2, 1]
                  },
                  opacity: {
                    duration: prefersReducedMotion ? 0 : 0.18
                  }
                }
              }} className="overflow-hidden">
                      <p className="font-sans pb-8 pr-12" style={{
                  marginTop: "4px",
                  fontSize: "clamp(1rem, 1vw, 1.0625rem)",
                  lineHeight: 1.65,
                  color: "var(--muted-foreground)",
                  maxWidth: "640px"
                }}>
                        {faq.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </motion.li>;
        })}
        </motion.ul>
      </div>
    </section>;
}